import type { LeverageInfo } from '@/features/tokenPairs';

/** Tier notionals arrive 1e18-scaled (see the backend's `usdTo1e18`). */
const NOTIONAL_SCALE = 1e18;
const BPS = 1e4;

/**
 * Hyperliquid's maintenance margin is half the initial margin at the tier's max
 * leverage — the same rule the backend encodes as
 * `liqLostP = (1 / maxLeverage) * 1e4 / 2`.
 */
function rateFromMaxLeverage(maxLeverage: number | null | undefined): number {
    return typeof maxLeverage === 'number' &&
        Number.isFinite(maxLeverage) &&
        maxLeverage > 0
        ? 1 / (2 * maxLeverage)
        : 0;
}

/**
 * The maintenance-margin fraction that decides where a position liquidates
 * (feed it to `estimateLiquidationPrice`). 0 = the market's margin terms are
 * unknown, which only happens before any market metadata has loaded.
 *
 * Sources in order of authority:
 *   1. the leverage tier covering `notionalUsd` — `liqLostP`, in bps;
 *   2. that tier's `maxLeverage`, halved, if the tier omits `liqLostP`;
 *   3. the market's own max leverage (the markets list carries it even when
 *      the per-market detail hasn't resolved).
 *
 * Tiers are ordered by notional and each entry's `maxNotionalUsd` is its upper
 * bound, so the first tier that still contains the position is the one that
 * applies; a position past the last bound keeps the last (strictest) tier.
 */
export function getMaintenanceRate(
    leverageInfo: LeverageInfo | null | undefined,
    marketMaxLeverage: number | null | undefined,
    notionalUsd: number | null | undefined,
): number {
    const tiers = leverageInfo?.tiers ?? [];
    if (tiers.length > 0) {
        const notional =
            typeof notionalUsd === 'number' && Number.isFinite(notionalUsd)
                ? notionalUsd
                : 0;
        const tier =
            tiers.find(
                (t) => notional <= Number(t.maxNotionalUsd) / NOTIONAL_SCALE,
            ) ?? tiers[tiers.length - 1];

        if (Number.isFinite(tier.liqLostP) && tier.liqLostP > 0) {
            return tier.liqLostP / BPS;
        }
        const fromTier = rateFromMaxLeverage(tier.maxLeverage);
        if (fromTier > 0) return fromTier;
    }

    return (
        rateFromMaxLeverage(leverageInfo?.maxLeverage) ||
        rateFromMaxLeverage(marketMaxLeverage)
    );
}
